import React, { useEffect, useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import Select from 'react-select'
import axiosAuthInstance from '../../../../utils/axios/axiosAuthInstance'
import { getPriorityColor } from '../../../../utils/Helper'
import { getRole } from '../../../../utils/cookies/cookies'
import { ProfileContext } from '../../../../context/profile'

const groupTasksByUser = (tasks) => {
  return tasks.reduce((acc, task) => {
    const assignKey = task.assignTo?._id || task.assignTo
    if (!acc[assignKey]) {
      acc[assignKey] = []
    }
    acc[assignKey].push(task)
    return acc
  }, {})
}

const TaskCard = ({ tasksList, reload }) => {
  const navigate = useNavigate()
  const role = getRole()
  const { profileData } = useContext(ProfileContext)

  const [groupedTasks, setGroupedTasks] = useState(null)
  const [taskStatus, setTaskStatus] = useState([])
  const [selectedStatus, setSelectedStatus] = useState({})
  const [taskTimelineMap, setTaskTimelineMap] = useState({})

  useEffect(() => {
    if (tasksList?.length > 0) {
      setGroupedTasks(groupTasksByUser(tasksList))
    }
  }, [tasksList])

  const fetchTaskStatus = async () => {
    try {
      const res = await axiosAuthInstance.get('task-status/dropdown')
      if (res.status === 200) {
        const formatted = res.data.result.map((item) => ({
          value: item._id,
          label: item.name,
        }))
        setTaskStatus(formatted)
      }
    } catch (error) {
      console.error('Error loading task statuses:', error)
    }
  }

  useEffect(() => {
    fetchTaskStatus()
  }, [])

  const handleStatusChange = async (selectedOption, taskId) => {
    console.log('Changing status to:', selectedOption)

    setSelectedStatus((prev) => ({
      ...prev,
      [taskId]: selectedOption ? selectedOption.value : '',
    }))

    try {
      const res = await axiosAuthInstance.put(`task/status/${taskId}`, {
        status: selectedOption?.value,
        isCompleted: selectedOption?.label === 'Final Submit',
      })
      if (res.status === 200) {
        reload()
      }
    } catch (error) {
      console.error('Status update failed:', error.response?.data || error)
    }
  }

  const fetchTimelineForTasks = async (tasks) => {
    try {
      const timelineMap = {}
      await Promise.all(
        tasks.map(async (task) => {
          const res = await axiosAuthInstance.get(`task-start-end/${task._id}`)
          if (res.status === 200 && res.data.data.length > 0) {
            const lastAction = res.data.data[res.data.data.length - 1].action
            timelineMap[task._id] = lastAction
          } else {
            timelineMap[task._id] = null
          }
        }),
      )
      setTaskTimelineMap(timelineMap)
    } catch (error) {
      console.error('Timeline fetch error:', error)
    }
  }

  useEffect(() => {
    if (tasksList?.length > 0) {
      fetchTimelineForTasks(tasksList)
      setGroupedTasks(groupTasksByUser(tasksList))
    }
  }, [tasksList])

  const handleTimelineAction = async (e, taskId, action) => {
    e.stopPropagation()

    const addedById = profileData?._id
    if (!addedById) {
      console.error('No user ID found in ProfileContext')
      return
    }

    const payload = {
      task: taskId,
      action,
      addedBy: addedById,
      addedByType: role === 'Admin' ? 'admin' : 'team',
    }

    console.log('Timeline payload:', payload)

    try {
      const res = await axiosAuthInstance.post('task-start-end/add', payload)

      if (res.status === 200) {
        setTaskTimelineMap((prev) => ({
          ...prev,
          [taskId]: action,
        }))

        // Change status after timeline update
        if (taskStatus.length === 0) {
          console.warn('taskStatus not loaded yet')
          return
        }

        let statusLabel = action === 'start' ? 'START' : 'Completed'
        const newStatus = taskStatus.find((s) => s.label === statusLabel)

        if (newStatus) {
          await handleStatusChange(newStatus, taskId)
        } else {
          console.warn(`Status label "${statusLabel}" not found in taskStatus`)
        }
      }
    } catch (error) {
      console.error('Timeline action failed:', error.response?.data || error)
    }
  }

  return (
    <>
      {role === 'HR' || role === 'Team Leader' || role === 'Social Media Coordinator' ? (
        <>
          {groupedTasks && Object.keys(groupedTasks).length > 0 ? (
            Object.keys(groupedTasks).map((user) => {
              const userTasks = groupedTasks[user]
              if (!userTasks || userTasks.length === 0) return null

              return (
                <div key={user} className='mb-8'>
                  <div className='bg-white rounded-lg w-full shadow-lg p-6 hover:shadow-xl transition-shadow'>
                    <h2 className='text-xl font-semibold text-center mb-4'>{user}</h2>
                    <div className='max-h-52 overflow-auto pr-2'>
                      {userTasks.map((task) => {
                        const priorityClass = getPriorityColor(task.priority)
                        return (
                          <div
                            key={task._id}
                            className='flex items-center justify-between border-b py-1 text-lg text-gray-900 mb-2 cursor-pointer'
                            onClick={() => navigate(`/task/view/${task._id}`)}
                          >
                            <span className='font-normal text-sm truncate w-full' title={task.name}>
                              {task.name}
                            </span>
                            <span className={`ml-5 px-3 py-1 text-sm rounded-full ${priorityClass}`}>
                              {task.priority}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )
            })
          ) : (
            <p>No Tasks</p>
          )}
        </>
      ) : (
        <>
          {tasksList?.length > 0 ? (
            tasksList.map((task) => {
              const priorityClass = getPriorityColor(task.priority)
              return (
                <div
                  key={task._id}
                  className='bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow mb-4'
                >
                  <h3
                    className='font-medium mb-2 truncate cursor-pointer'
                    onClick={() => navigate(`/task/view/${task._id}`)}
                    title={task.name}
                  >
                    {task.name}
                  </h3>
                  <div>
                    <span className={`px-3 py-1 text-sm rounded-full ${priorityClass}`}>
                      {task.priority}
                    </span>
                    <span className='ml-3 text-sm text-gray-900'>{task.company}</span>
                    <div className='mt-2'>
                      <span className='font-medium mr-1'>Status:</span>{' '}
                      {task?.status || 'Not Started Yet'}
                    </div>
                    {taskTimelineMap[task._id] !== 'start' ? (
                      <button
                        onClick={(e) => handleTimelineAction(e, task._id, 'start')}
                        className='bg-green-500 text-white px-3 py-1 rounded mt-2'
                      >
                        Start
                      </button>
                    ) : (
                      <button
                        onClick={(e) => handleTimelineAction(e, task._id, 'end')}
                        className='bg-red-500 text-white px-3 py-1 rounded mt-2'
                      >
                        End
                      </button>
                    )}
                  </div>
                  <Select
                    id='taskStatus'
                    value={taskStatus.find((s) => s.value === selectedStatus[task._id])}
                    onChange={(option) => handleStatusChange(option, task._id)}
                    options={taskStatus}
                    placeholder='Task Status'
                    className='mt-3'
                    classNamePrefix='react-select'
                  />
                </div>
              )
            })
          ) : (
            <p>No Tasks</p>
          )}
        </>
      )}
    </>
  )
}

export default TaskCard
